import { type FC, useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Checkbox,
} from '@/components/ui';
import { supabase } from '@/lib/supabase';

const LOGO_BUCKET = 'vendor-logos';
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
];

interface DiscountInfo {
  code: string;
  description: string | null;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  baseFeeCents: number;
  discountAmountCents: number;
  finalCents: number;
}

const formatMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const DEFAULT_BASE_FEE_CENTS = 5000;

export const VendorRegistrationPage: FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [slugChecked, setSlugChecked] = useState(false);
  const [slugValid, setSlugValid] = useState(false);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [taxAcknowledged, setTaxAcknowledged] = useState(false);
  const [contactIsStaff, setContactIsStaff] = useState(false);
  const [staffName, setStaffName] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  const [staffName2, setStaffName2] = useState('');
  const [staffEmail2, setStaffEmail2] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState<DiscountInfo | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [canceledNotice, setCanceledNotice] = useState(false);

  // Verify the slug server-side on mount. The validate-vendor-code edge function
  // returns { slugValid: true } when the slug matches site_settings.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.functions.invoke('validate-vendor-code', {
        body: { slug },
      });
      if (cancelled) return;
      setSlugValid(Boolean(data?.slugValid));
      setSlugChecked(true);
    })();

    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('canceled') === 'true') setCanceledNotice(true);
    }

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const baseFeeCents = discount?.baseFeeCents ?? DEFAULT_BASE_FEE_CENTS;
  const finalCents = discount ? discount.finalCents : baseFeeCents;

  const handleApplyCode = async () => {
    setCodeError(null);
    setDiscount(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setCodeError('Enter a code');
      return;
    }

    setValidatingCode(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-vendor-code', {
        body: { slug, code: trimmed },
      });
      if (error) {
        setCodeError('Could not validate code. Try again.');
        return;
      }
      if (!data?.isValid) {
        setCodeError(data?.error || 'Invalid code');
        return;
      }
      setDiscount({
        code: trimmed,
        description: data.description ?? null,
        discountType: data.discountType,
        discountValue: data.discountValue,
        baseFeeCents: data.baseFeeCents,
        discountAmountCents: data.discountAmountCents,
        finalCents: data.finalCents,
      });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleClearCode = () => {
    setDiscount(null);
    setCode('');
    setCodeError(null);
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLogoError(null);
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError('Logo must be a JPEG, PNG, WebP, GIF, or SVG image.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`Logo must be under ${MAX_LOGO_BYTES / 1024 / 1024}MB.`);
      e.target.value = '';
      return;
    }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleClearLogo = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const uploadLogo = async (): Promise<{ url: string; path: string } | { error: string } | null> => {
    if (!logoFile) return null;
    const ext = logoFile.name.split('.').pop()?.toLowerCase() || 'png';
    const filePath = `submissions/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
    const { data, error } = await supabase.storage
      .from(LOGO_BUCKET)
      .upload(filePath, logoFile, { cacheControl: '3600', upsert: false });
    if (error || !data) {
      return { error: error?.message || 'Logo upload failed' };
    }
    const { data: urlData } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(data.path);
    return { url: urlData.publicUrl, path: data.path };
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!contactName.trim() || !contactEmail.trim() || !organizationName.trim()) {
      setFormError('Please fill in all required fields');
      return;
    }
    if (!contactEmail.includes('@')) {
      setFormError('Please enter a valid contact email');
      return;
    }
    if (!taxAcknowledged) {
      setFormError('You must acknowledge tax liability to register');
      return;
    }
    if (!contactIsStaff && staffEmail.trim() && !staffEmail.includes('@')) {
      setFormError('Please enter a valid email for the first person running the booth, or leave it blank');
      return;
    }
    if (staffEmail2.trim() && !staffEmail2.includes('@')) {
      setFormError('Please enter a valid email for the second person running the booth, or leave it blank');
      return;
    }
    if (!contactIsStaff && Boolean(staffName.trim()) !== Boolean(staffEmail.trim())) {
      setFormError('Please provide both a name and email for the first person running the booth, or leave both blank');
      return;
    }
    if (Boolean(staffName2.trim()) !== Boolean(staffEmail2.trim())) {
      setFormError('Please provide both a name and email for the second person running the booth, or leave both blank');
      return;
    }

    setSubmitting(true);
    try {
      const logoResult = await uploadLogo();
      if (logoResult && 'error' in logoResult) {
        setFormError(logoResult.error);
        setSubmitting(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('vendor-checkout', {
        body: {
          slug,
          contactName: contactName.trim(),
          contactEmail: contactEmail.trim(),
          organizationName: organizationName.trim(),
          taxAcknowledged: true,
          contactIsStaff,
          staffName: contactIsStaff ? null : staffName.trim() || null,
          staffEmail: contactIsStaff ? null : staffEmail.trim() || null,
          staffName2: staffName2.trim() || null,
          staffEmail2: staffEmail2.trim() || null,
          logoPath: logoResult?.path ?? null,
          code: discount?.code ?? null,
        },
      });

      if (error) {
        setFormError((error as Error).message || 'Checkout failed');
        return;
      }
      if (data?.error) {
        setFormError(data.error);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.waived && data?.vendorRegistrationId) {
        navigate(`/confirmation?vendor=1&id=${data.vendorRegistrationId}`);
        return;
      }
      setFormError('Unexpected response from server');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!slugChecked) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <p className="text-turquoise-700">Loading...</p>
      </div>
    );
  }

  if (!slugValid) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-white border-turquoise-200 shadow-md">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-viking text-turquoise-900">Page not found</CardTitle>
            <CardDescription className="text-turquoise-700">
              The page you're looking for doesn't exist.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-turquoise-100 text-turquoise-900 border-turquoise-300 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-viking text-turquoise-900">Vendor Registration</CardTitle>
              <CardDescription className="text-turquoise-700">
                Reserve your booth at Cactus Cup. Includes two free entries: yourself and one staff helper.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {canceledNotice && (
                <div className="mb-4 bg-yellow-50 text-yellow-900 text-sm p-3 rounded-md border border-yellow-300">
                  Checkout was canceled. Your information is not saved. Submit again to retry payment.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {formError && (
                  <div className="bg-red-900/40 text-red-200 text-sm p-3 rounded-md border border-red-500/40">
                    {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Contact name *</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Contact email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization / business name *</Label>
                  <Input
                    id="org-name"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    autoComplete="organization"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo-input" className="text-turquoise-900 font-semibold">
                    Logo (optional)
                  </Label>
                  <p className="text-xs text-turquoise-700">
                    Upload a JPEG, PNG, WebP, GIF, or SVG, max 5MB.
                  </p>
                  <div className="flex items-center gap-3 pt-1">
                    <Input
                      id="logo-input"
                      ref={logoInputRef}
                      type="file"
                      accept={ALLOWED_LOGO_TYPES.join(',')}
                      onChange={handleLogoChange}
                      className="cursor-pointer"
                    />
                    {logoPreview && (
                      <>
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-12 w-12 object-contain rounded border border-turquoise-200"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={handleClearLogo}>
                          Remove
                        </Button>
                      </>
                    )}
                  </div>
                  {logoError && <p className="text-sm text-red-600">{logoError}</p>}
                </div>

                <div className="bg-turquoise-50 border border-turquoise-200 rounded-md p-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="tax-ack"
                        checked={taxAcknowledged}
                        onCheckedChange={(checked) => setTaxAcknowledged(checked === true)}
                        className="border-turquoise-700"
                      />
                      <Label htmlFor="tax-ack" className="text-sm text-turquoise-900 leading-relaxed cursor-pointer">
                        I understand that as a vendor I am responsible for collecting and remitting any
                        applicable sales tax, and that I am liable for the legality and safety of products
                        sold at my booth. *
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-turquoise-900 font-semibold">Who is running the booth</Label>
                    <p className="text-xs text-turquoise-700">
                      Up to two people get free weekend entry to run the booth. Both fields are optional.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="contact-is-staff"
                      checked={contactIsStaff}
                      onCheckedChange={(checked) => setContactIsStaff(checked === true)}
                      className="border-turquoise-700"
                    />
                    <Label
                      htmlFor="contact-is-staff"
                      className="text-sm text-turquoise-900 leading-relaxed cursor-pointer"
                    >
                      The organization contact above is one of the people running the booth.
                    </Label>
                  </div>

                  {!contactIsStaff && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                      <div className="space-y-2">
                        <Label htmlFor="staff-name" className="text-sm">Person 1 name</Label>
                        <Input
                          id="staff-name"
                          type="text"
                          value={staffName}
                          onChange={(e) => setStaffName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="staff-email" className="text-sm">Person 1 email</Label>
                        <Input
                          id="staff-email"
                          type="email"
                          value={staffEmail}
                          onChange={(e) => setStaffEmail(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                    <div className="space-y-2">
                      <Label htmlFor="staff-name-2" className="text-sm">
                        {contactIsStaff ? 'Other person name' : 'Person 2 name'}
                      </Label>
                      <Input
                        id="staff-name-2"
                        type="text"
                        value={staffName2}
                        onChange={(e) => setStaffName2(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="staff-email-2" className="text-sm">
                        {contactIsStaff ? 'Other person email' : 'Person 2 email'}
                      </Label>
                      <Input
                        id="staff-email-2"
                        type="email"
                        value={staffEmail2}
                        onChange={(e) => setStaffEmail2(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-turquoise-300 pt-5 space-y-3">
                  <Label htmlFor="code" className="text-turquoise-900 font-semibold">Discount code (optional)</Label>
                  {!discount ? (
                    <div className="flex gap-2">
                      <Input
                        id="code"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Enter code"
                        disabled={validatingCode}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyCode}
                        disabled={validatingCode || !code.trim()}
                      >
                        {validatingCode ? 'Checking...' : 'Apply'}
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-300 text-green-900 rounded-md p-3 flex items-center justify-between">
                      <div className="text-sm">
                        <div className="font-semibold">Code <span className="font-mono">{discount.code}</span> applied</div>
                        {discount.description && <div className="text-xs">{discount.description}</div>}
                        <div className="text-xs">
                          {discount.discountType === 'percent'
                            ? `${discount.discountValue}% off`
                            : `${formatMoney(discount.discountValue)} off`}
                          {', '}you save {formatMoney(discount.discountAmountCents)}
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={handleClearCode}>
                        Remove
                      </Button>
                    </div>
                  )}
                  {codeError && (
                    <p className="text-sm text-red-600">{codeError}</p>
                  )}
                </div>

                <div className="bg-gray-100 rounded-md p-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Table fee</span>
                    <span>{formatMoney(baseFeeCents)}</span>
                  </div>
                  {discount && discount.discountAmountCents > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Discount ({discount.code})</span>
                      <span>-{formatMoney(discount.discountAmountCents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2 border-t border-gray-300">
                    <span>Total</span>
                    <span>{formatMoney(finalCents)}</span>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting
                    ? 'Submitting...'
                    : finalCents === 0
                      ? 'Confirm registration'
                      : `Pay ${formatMoney(finalCents)} and register`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
